import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// El panel de un área: la devolución del relevamiento, para An.
//
// Se abre con la clave secreta del área (/area/:clave), que nunca se le pasa a
// la gente que se entrevista. Muestra cuánto se relevó, el mapa del área que
// cruza todas las entrevistas, cada dossier y lo que costó. El estilo es el de
// la plantilla de documento de consultoría de GUIA (guia-memory/_plantillas):
// claro, con aire, acentos en mint, sin emojis; y se baja en PDF.

const API = '/.netlify/functions/entrevistador-panel';

const C = {
  tinta: '#1F2937',
  gris: '#6B7280',
  linea: '#E5E7EB',
  mint: '#29D880',
  mintClaro: '#F0FBF5',
  aviso: '#B45309',
  avisoClaro: '#FFF7ED',
};

const ESTADOS = {
  en_curso: 'En curso',
  cerrada: 'Cerrada, se está armando el dossier',
  con_dossier: 'Con dossier',
  dossier_fallo: 'El dossier no se pudo armar',
};

const fecha = iso => (iso ? new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' }) : '');
const usd = n => `USD ${(n || 0).toFixed(2)}`;
const lleno = v => (Array.isArray(v) ? v.length > 0 : Boolean(v && String(v).trim()));

// ── Piezas ────────────────────────────────────────────────────────────────
const Seccion = ({ titulo, children, visible = true }) => (visible ? (
  <section className="mt-12" style={{ breakInside: 'auto' }}>
    <h2 className="text-lg font-semibold mb-4" style={{ color: C.tinta, breakAfter: 'avoid' }}>{titulo}</h2>
    {children}
  </section>
) : null);

const Punto = ({ children }) => (
  <li className="flex gap-3 leading-relaxed">
    <span className="mt-2.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: C.mint }} />
    <span>{children}</span>
  </li>
);

const Lista = ({ items, render }) => (
  lleno(items) ? <ul className="space-y-2">{items.map((x, i) => <Punto key={i}>{render(x)}</Punto>)}</ul> : null
);

const Dato = ({ etiqueta, children }) => (
  lleno(children) ? (
    <p className="leading-relaxed"><span style={{ color: C.gris }}>{etiqueta}: </span>{children}</p>
  ) : null
);

const Marca = ({ tono = 'mint', children }) => (
  <span
    className="inline-block text-xs px-2 py-0.5 rounded-full align-middle"
    style={tono === 'mint'
      ? { background: C.mintClaro, color: '#166534' }
      : { background: C.avisoClaro, color: C.aviso }}
  >
    {children}
  </span>
);

const Nota = ({ children }) => (
  <div className="rounded-r-md px-5 py-4 leading-relaxed" style={{ background: C.mintClaro, borderLeft: `3px solid ${C.mint}` }}>
    {children}
  </div>
);

const Proceso = ({ p }) => (
  <div className="py-5" style={{ borderTop: `1px solid ${C.linea}`, breakInside: 'avoid' }}>
    <p className="font-semibold" style={{ color: C.tinta }}>
      {p.nombre}{' '}
      {lleno(p.puesto_dueno) ? <span className="font-normal" style={{ color: C.gris }}>· lo lleva {p.puesto_dueno}</span> : <Marca tono="aviso">sin dueño</Marca>}
    </p>
    <div className="mt-2 space-y-1">
      <Dato etiqueta="Para qué">{p.para_que}</Dato>
      <Dato etiqueta="Arranca cuando">{p.disparador}</Dato>
      {lleno(p.pasos) && (
        <ol className="mt-2 space-y-1">
          {p.pasos.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: C.mint, color: '#fff' }}>{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      )}
      <Dato etiqueta="Recibe">{(p.recibe || []).map(r => `${r.que} (de ${r.de_quien})`).join('; ')}</Dato>
      <Dato etiqueta="Entrega">{(p.entrega || []).map(r => `${r.que} (a ${r.a_quien})`).join('; ')}</Dato>
      <Dato etiqueta="Con">{(p.herramientas || []).join(', ')}</Dato>
      <Dato etiqueta="Decide solo">{p.decide_solo}</Dato>
      <Dato etiqueta="Frecuencia">{p.frecuencia}</Dato>
      <Dato etiqueta="Dónde se rompe">{p.donde_se_rompe}</Dato>
      <Dato etiqueta="Lo contaron">{(p.lo_contaron || []).join(', ')}</Dato>
      {lleno(p.cita) && <p className="italic" style={{ color: C.gris }}>«{p.cita}»</p>}
    </div>
  </div>
);

const Tabla = ({ columnas, filas }) => (
  lleno(filas) ? (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>{columnas.map(c => <th key={c.k} className="text-left font-medium py-2 pr-4" style={{ color: C.gris, borderBottom: `1px solid ${C.linea}` }}>{c.t}</th>)}</tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>{columnas.map(c => <td key={c.k} className="py-2 pr-4 align-top" style={{ borderBottom: `1px solid ${C.linea}` }}>{f[c.k]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null
);

// ── El mapa del área ──────────────────────────────────────────────────────
const MapaDelArea = ({ m }) => {
  const tec = m.lectura_tecnica || {};
  const com = m.lectura_comercial || {};
  return (
    <>
      {lleno(m.para_que_existe_el_area) && <Nota>{m.para_que_existe_el_area}</Nota>}

      <Seccion titulo="Puestos" visible={lleno(m.puestos)}>
        <Lista items={m.puestos} render={x => <><strong>{x.puesto}</strong> ({x.cuantos}; entrevistados: {x.entrevistados}). {x.que_hace}</>} />
      </Seccion>

      <Seccion titulo="Procesos" visible={lleno(m.procesos)}>
        {(m.procesos || []).map((p, i) => <Proceso key={i} p={p} />)}
      </Seccion>

      <Seccion titulo="Pedidos entre puestos" visible={lleno(m.pedidos_cruzados)}>
        <Lista items={m.pedidos_cruzados} render={x => (
          <>
            {x.quien_pide} → {x.a_quien}: {x.que}{' '}
            <Marca tono={x.lo_confirma_el_otro === 'sí' ? 'mint' : 'aviso'}>
              {{ sí: 'lo confirma el otro', no: 'el otro no lo menciona', distinto: 'el otro lo cuenta distinto', 'no entrevistado': 'falta entrevistar al otro' }[x.lo_confirma_el_otro] || x.lo_confirma_el_otro}
            </Marca>
            {lleno(x.observacion) && <span style={{ color: C.gris }}> {x.observacion}</span>}
          </>
        )} />
      </Seccion>

      <Seccion titulo="Para resolver con el área" visible={lleno(m.contradicciones)}>
        <Lista items={m.contradicciones} render={x => (
          <><strong>{x.tema}.</strong> «{x.una_version}» y «{x.otra_version}». <span style={{ color: C.gris }}>{x.que_hay_que_resolver}</span></>
        )} />
      </Seccion>

      <Seccion titulo="Dolores" visible={lleno(m.dolores)}>
        <Lista items={m.dolores} render={x => <>{x.dolor} <span style={{ color: C.gris }}>({x.cuantos_lo_mencionan}; {x.cuanto_cuesta})</span></>} />
      </Seccion>

      <Seccion titulo="Los datos: quién crea, cambia y lee" visible={lleno(m.datos)}>
        <Tabla
          columnas={[{ k: 'que_es', t: 'Qué' }, { k: 'donde_vive', t: 'Dónde vive' }, { k: 'quien_la_crea', t: 'Crea' }, { k: 'quien_la_cambia', t: 'Cambia' }, { k: 'quien_la_lee', t: 'Lee' }, { k: 'cargada_en_mas_de_un_lugar', t: 'También en' }]}
          filas={m.datos}
        />
      </Seccion>

      <Seccion titulo="Archivos" visible={lleno(m.archivos)}>
        <Tabla
          columnas={[{ k: 'archivo', t: 'Archivo' }, { k: 'que_es', t: 'Qué es' }, { k: 'donde_esta', t: 'Dónde está' }, { k: 'a_donde_va', t: 'A dónde va' }, { k: 'alerta', t: 'Atención' }]}
          filas={m.archivos}
        />
      </Seccion>

      <Seccion titulo="Cuidado de la información" visible={lleno(m.seguridad)}>
        <Lista items={m.seguridad} render={x => (
          <><Marca tono={x.gravedad === 'alto' ? 'aviso' : 'mint'}>{x.gravedad}</Marca> {x.hallazgo}. <span style={{ color: C.gris }}>Buena práctica: {x.buena_practica}</span></>
        )} />
      </Seccion>

      <Seccion titulo="Oportunidades" visible={lleno(m.oportunidades)}>
        <Lista items={m.oportunidades} render={x => (
          <><strong>{x.oportunidad}.</strong> {x.por_que} <span style={{ color: C.gris }}>Impacto {x.impacto}, esfuerzo {x.esfuerzo}.</span></>
        )} />
      </Seccion>

      <Seccion titulo="Lectura técnica" visible={lleno(tec.resumen)}>
        <div className="space-y-2">
          <p className="leading-relaxed">{tec.resumen}</p>
          <Dato etiqueta="Límites del área">{tec.limites_del_area}</Dato>
          <Dato etiqueta="Fuente de verdad">{tec.fuente_de_verdad_propuesta}</Dato>
          <Dato etiqueta="Integraciones">{(tec.integraciones || []).join('; ')}</Dato>
          <Dato etiqueta="Desperdicios">{(tec.desperdicios || []).join('; ')}</Dato>
        </div>
      </Seccion>

      <Seccion titulo="Lectura comercial" visible={lleno(com.por_que) || lleno(com.tamano)}>
        <div className="space-y-2">
          <Dato etiqueta="Plan">{com.plan_sugerido && com.plan_sugerido !== 'sin datos' ? `${com.plan_sugerido}. ${com.por_que || ''}` : ''}</Dato>
          <Dato etiqueta="Tamaño">{com.tamano}</Dato>
          <Dato etiqueta="Decide">{com.quien_decide}</Dato>
          <Dato etiqueta="Urgencia">{com.urgencia}</Dato>
          <Dato etiqueta="Señales">{(com.senales || []).join('; ')}</Dato>
          <Dato etiqueta="Objeciones">{(com.objeciones || []).join('; ')}</Dato>
        </div>
      </Seccion>

      <Seccion titulo="Falta relevar" visible={lleno(m.faltantes)}>
        <Lista items={m.faltantes} render={x => <>{x.que} <span style={{ color: C.gris }}>(lo sabe: {x.quien_lo_sabe || 'a definir'})</span></>} />
      </Seccion>
    </>
  );
};

// ── Un dossier ────────────────────────────────────────────────────────────
const Dossier = ({ d }) => (
  <div className="space-y-6 pt-4">
    {lleno(d.para_que_existe_el_area) && <Dato etiqueta="Para qué existe el área, según este puesto">{d.para_que_existe_el_area}</Dato>}
    {lleno(d.procesos) && <div>{d.procesos.map((p, i) => <Proceso key={i} p={p} />)}</div>}
    {[
      ['Lo que solo sabe este puesto', d.lo_que_solo_sabe, x => <>{x.saber} <span style={{ color: C.gris }}>(riesgo: {x.riesgo})</span></>],
      ['Lo que se repite', d.lo_que_se_repite, x => <>{x.tarea} <span style={{ color: C.gris }}>({x.cuanto}; {x.candidato_a})</span></>],
      ['Dolores', d.dolores, x => <>{x.dolor}{lleno(x.cita) && <span className="italic" style={{ color: C.gris }}> «{x.cita}»</span>}</>],
      ['Pedidos a otras áreas', d.pedidos_entre_areas, x => <>{x.quien_pide} → {x.a_quien}: {x.que}</>],
      ['Cuidado de la información', d.seguridad, x => <>{x.hallazgo}. <span style={{ color: C.gris }}>{x.buena_practica}</span></>],
      ['Oportunidades', d.oportunidades, x => <><strong>{x.oportunidad}.</strong> {x.por_que}</>],
      ['Falta relevar', d.faltantes, x => <>{x.que} <span style={{ color: C.gris }}>(lo sabe: {x.quien_lo_sabe || 'a definir'})</span></>],
    ].filter(([, items]) => lleno(items)).map(([titulo, items, render]) => (
      <div key={titulo}>
        <p className="font-semibold mb-2" style={{ color: C.tinta }}>{titulo}</p>
        <Lista items={items} render={render} />
      </div>
    ))}
  </div>
);

// ── La página ─────────────────────────────────────────────────────────────
const PanelArea = () => {
  const { clave } = useParams();
  const [panel, setPanel] = useState(null);
  const [estado, setEstado] = useState('cargando');
  const [abiertos, setAbiertos] = useState({});
  const [copiado, setCopiado] = useState(false);

  // Que ningún buscador lo indexe, aunque el link se filtre.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    fetch(`${API}?clave=${encodeURIComponent(clave)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setPanel(d); setEstado('listo'); })
      .catch(() => setEstado('inexistente'));
  }, [clave]);

  const linkEntrevista = panel ? `${window.location.origin}/entrevista/${panel.area.slug}` : '';

  const copiar = () => {
    try {
      navigator.clipboard.writeText(linkEntrevista);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {}
  };

  // Para el PDF, los dossiers van abiertos.
  const bajarPdf = () => {
    setAbiertos(Object.fromEntries((panel?.entrevistas || []).map((_, i) => [i, true])));
    setTimeout(() => window.print(), 100);
  };

  if (estado !== 'listo') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#fff', color: C.tinta, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
        <p style={{ color: C.gris }}>{estado === 'cargando' ? 'Cargando…' : 'Este panel no existe o el link está incompleto.'}</p>
      </div>
    );
  }

  const { area, entrevistas, mapa } = panel;
  const cerradas = entrevistas.filter(e => e.estado === 'con_dossier').length;
  const enCurso = entrevistas.filter(e => e.estado === 'en_curso').length;
  const m = mapa?.mapa;
  const cob = m?.cobertura;

  return (
    <div className="min-h-screen" style={{ background: '#fff', color: C.tinta, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <style>{`
        @media print {
          .no-imprimir { display: none !important; }
          @page { margin: 18mm 16mm; }
        }
      `}</style>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: C.mint }}>GUIA · Relevamiento del área</p>
        <h1 className="text-3xl md:text-4xl font-semibold mt-3 leading-tight">{area.nombre}</h1>
        <p className="mt-2" style={{ color: C.gris }}>{area.empresa} · {area.rubro}</p>
        <div className="w-12 h-0.5 mt-6" style={{ background: C.mint }} />

        <Seccion titulo="El relevamiento">
          <div className="space-y-3">
            <p className="leading-relaxed">
              <strong>{cerradas}</strong> {cerradas === 1 ? 'entrevista con dossier' : 'entrevistas con dossier'}
              {enCurso > 0 && <>, <strong>{enCurso}</strong> en curso</>}.
              {cob && <> {cob.alcanza_para_la_devolucion
                ? <Marca>alcanza para la devolución</Marca>
                : <Marca tono="aviso">todavía no alcanza para la devolución</Marca>}</>}
            </p>
            {cob?.por_que && <p style={{ color: C.gris }}>{cob.por_que}</p>}
            {lleno(cob?.puestos_que_faltan) && (
              <div>
                <p className="font-semibold mb-2">Falta entrevistar</p>
                <Lista items={cob.puestos_que_faltan} render={x => <><strong>{x.puesto}</strong>. <span style={{ color: C.gris }}>{x.por_que}</span></>} />
              </div>
            )}
            <div className="no-imprimir flex flex-wrap items-center gap-3 pt-2">
              <span className="text-sm" style={{ color: C.gris }}>Link para la gente del área:</span>
              <code className="text-sm px-2 py-1 rounded break-all max-w-full" style={{ background: '#F9FAFB' }}>{linkEntrevista.replace(/^https?:\/\//, '')}</code>
              <button onClick={copiar} className="text-sm px-3 py-1 rounded-full" style={{ border: `1px solid ${C.mint}`, color: '#166534' }}>
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
              {!area.activa && <Marca tono="aviso">link cerrado</Marca>}
            </div>
          </div>
        </Seccion>

        {m ? (
          <>
            <div className="mt-14" style={{ borderTop: `1px solid ${C.linea}` }} />
            <p className="text-xs font-semibold tracking-widest uppercase mt-10" style={{ color: C.mint }}>El mapa del área</p>
            <p className="text-sm mt-1" style={{ color: C.gris }}>Cruza {mapa.entrevistas} {mapa.entrevistas === 1 ? 'entrevista' : 'entrevistas'} · al {fecha(mapa.updated_at)}</p>
            <div className="mt-6"><MapaDelArea m={m} /></div>
          </>
        ) : (
          <Seccion titulo="El mapa del área">
            <p style={{ color: C.gris }}>Se arma solo cuando se cierra la primera entrevista.</p>
          </Seccion>
        )}

        <Seccion titulo="Las entrevistas" visible={lleno(entrevistas)}>
          <div>
            {entrevistas.map((e, i) => (
              <details
                key={i}
                open={!!abiertos[i]}
                onToggle={ev => setAbiertos(a => ({ ...a, [i]: ev.currentTarget.open }))}
                className="py-4"
                style={{ borderTop: `1px solid ${C.linea}`, breakInside: 'auto' }}
              >
                <summary className="cursor-pointer list-none flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{e.puesto || (e.estado === 'en_curso' ? 'Entrevista en curso' : 'Puesto sin dato')}{e.persona && <span className="font-normal" style={{ color: C.gris }}> · {e.persona}</span>}</span>
                  <span className="text-sm" style={{ color: C.gris }}>
                    {fecha(e.empezo)} · {e.respuestas} respuestas · {ESTADOS[e.estado] || e.estado} · {usd(e.usd)}
                  </span>
                </summary>
                {e.dossier ? <Dossier d={e.dossier} /> : e.error ? <p className="pt-3" style={{ color: C.aviso }}>{e.error}</p> : null}
              </details>
            ))}
          </div>
        </Seccion>

        <div className="mt-14 pt-6 flex flex-wrap items-center justify-between gap-4" style={{ borderTop: `1px solid ${C.linea}` }}>
          <p className="text-sm" style={{ color: C.gris }}>Costo de la IA en esta área: <strong style={{ color: C.tinta }}>{usd(panel.usd)}</strong></p>
          <button onClick={bajarPdf} className="no-imprimir text-sm px-4 py-2 rounded-full font-medium" style={{ background: C.mint, color: '#fff' }}>
            Bajar en PDF
          </button>
        </div>
        <p className="text-xs mt-10" style={{ color: C.gris }}>GUIA · Antonela Dutruel · Consultoría en Inteligencia Artificial y Producto</p>
      </main>
    </div>
  );
};

export default PanelArea;
